"use client";

import { GROUP, GROUP_STATS, VERTICALS } from "@/data/about-content";
import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { Building2, Factory, Truck, Zap, type LucideIcon } from "lucide-react";
import { useLocale } from "next-intl";

const ICONS: Record<string, LucideIcon> = {
  building2: Building2,
  truck: Truck,
  zap: Zap,
  factory: Factory,
};

interface AboutContent { groupName: Bilingual; groupBody: Bilingual }

/** THE GROUP — parent group name/description (panel-managed) + stats & verticals. */
export default function AboutGroup() {
  const locale = useLocale() as Locale;
  const { data } = useContent<AboutContent>("about");
  const title = data?.groupName?.[locale] ?? GROUP.title[locale];
  const body = data?.groupBody?.[locale] ?? GROUP.body[locale];

  return (
    <section id="group" className="scroll-mt-44 bg-[#0f0f0f] text-white">
      <div className="mx-auto grid max-w-360 gap-14 px-6 py-20 md:py-28 lg:grid-cols-2 lg:gap-20 lg:px-10">
        {/* Narrative + stats */}
        <div>
          <span className="text-[11px] uppercase tracking-[0.22em] text-accent">{GROUP.eyebrow[locale]}</span>
          <h2 className="mt-4 font-bebas text-[clamp(2.6rem,6vw,5rem)] uppercase leading-[0.9]">{title}</h2>
          <p className="mt-6 max-w-md leading-relaxed text-white/70">{body}</p>

          <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-10">
            {GROUP_STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="font-bebas text-[clamp(2.4rem,5vw,3.6rem)] leading-none text-accent">{s.value}</div>
                <div className="mt-2 text-[11px] uppercase tracking-widest text-white/45">{s.label[locale]}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Verticals */}
        <div className="flex flex-col justify-center">
          {VERTICALS.map((v, i) => {
            const Icon = ICONS[v.icon] ?? Building2;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group flex items-start justify-between gap-6 border-b border-white/10 py-6 transition-colors first:border-t hover:bg-white/[0.03]"
              >
                <div>
                  <h3 className="font-bebas text-2xl uppercase tracking-wide">{v.title[locale]}</h3>
                  <p className="mt-1 text-sm text-white/45">{v.desc[locale]}</p>
                </div>
                <Icon className="mt-1 h-6 w-6 shrink-0 text-accent transition-transform duration-300 group-hover:scale-110" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
